import type {
  ChatMessage,
  IntelligenceContext,
  MultimodalContextData,
} from '../types.js'
import { MIN_USER_TURNS_BEFORE_SCORING } from '../../contracts.js'
import type {
  ConversationCategory,
  ConversationFlowState,
  ConversationObjective,
} from '../../../types/conversation-flow-types.js'
import { isWeekOnePlanAsk, lastUserTextFromMessages } from './consulting-move.js'

export type ConsultantSegment =
  | 'local_service_company'
  | 'small_business_owner'
  | 'startup_founder'
  | 'marketing_manager'
  | 'cto_product_lead'
  | 'enterprise_buyer'
  | 'intern_student'
  | 'unknown'

export type BuyingIntent = 'none' | 'low' | 'medium' | 'high'

export type ServiceFitMode =
  | 'basic_ai_workshop'
  | 'team_workshop'
  | 'consulting_advisory'
  | 'implementation_sprint'
  | 'enterprise_transformation'
  | 'no_sale_education'
  | 'diagnostic'

export type MultimodalOpportunity = 'screen_share' | 'upload' | 'url' | 'webcam' | null

export interface ConsultantState {
  identityConfidence: 'none' | 'provisional' | 'verified'
  segment: ConsultantSegment
  corePainCaptured: boolean
  buyingIntent: BuyingIntent
  urgency: 'low' | 'medium' | 'high'
  serviceFit: ServiceFitMode
  evidenceAvailable: boolean
  multimodalOpportunity: MultimodalOpportunity
  bookingReadiness: 'not_ready' | 'emerging' | 'ready'
  shouldOfferServiceFit: boolean
  suggestedNextCategory: ConversationCategory | null
  conversationObjective: ConversationObjective
  fitScore: { workshop: number; consulting: number }
  signals: string[]
}

export interface ServiceFitPitchConfig {
  product: 'workshop' | 'consulting'
  serviceMode: ServiceFitMode
  name: string
  priceRange: string
  duration: string
  format: string
  bestFor: string
  positioning: string
}

interface AnalyzeConsultantStateParams {
  messages?: ChatMessage[] | undefined
  conversationFlow?: Partial<ConversationFlowState> | undefined
  intelligenceContext?: IntelligenceContext | undefined
  multimodalContext?: MultimodalContextData | undefined
}

const DEFAULT_FIT_SCORE = { workshop: 0.35, consulting: 0.35 }

const LOCAL_SERVICE_PATTERNS = [
  /\bplumb(?:er|ing)?\b/i,
  /\belectric(?:ian|al)?\b/i,
  /\bhvac\b/i,
  /\bclinic\b/i,
  /\bdentist\b/i,
  /\brestaurant\b/i,
  /\bconstruction\b/i,
  /\bmiss(?:ed|ing)? calls?\b/i,
  /\bscheduling\b/i,
  /\bestimates?\b/i,
]

const MARKETING_PATTERNS = [
  /\bmarketing\b/i,
  /\bhubspot\b/i,
  /\bgoogle analytics\b/i,
  /\bga4\b/i,
  /\bgmail\b/i,
  /\bcampaigns?\b/i,
  /\bleads?\b/i,
]

const CRM_AUTOMATION_PATTERNS = [
  /\bcrm\b/i,
  /\bhubspot\b/i,
  /\bsalesforce\b/i,
  /\bgmail\b/i,
  /\bsupport inbox\b/i,
  /\bzendesk\b/i,
  /\bautomation\b/i,
  /\bintegrat(?:e|ion|ing)\b/i,
  /\bworkflow\b/i,
]

const ENTERPRISE_PATTERNS = [
  /\benterprise\b/i,
  /\bfortune\s*500\b/i,
  /\b1000\+?\s*(?:employees|people|staff)?\b/i,
  /\bglobal team\b/i,
  /\bgovernance\b/i,
  /\bcompliance\b/i,
  /\bsecurity\b/i,
  /\bchange management\b/i,
]

const INTERN_PATTERNS = [
  /\bintern\b/i,
  /\bstudent\b/i,
  /\bassignment\b/i,
  /\bhomework\b/i,
  /\bschool\b/i,
  /\buniversity\b/i,
  /\bnot (?:a|the) buyer\b/i,
  /\bnot buying\b/i,
  /\bjust (?:testing|learning|browsing)\b/i,
  /\bi['’]?m (?:just )?learning\b/i,
  /\bfor myself\b/i,
  /\bpersonal (?:learning|project|use)\b/i,
  /\bjust testing\b/i,
  /\btesting this\b/i,
]

const WORKSHOP_PATTERNS = [
  /\bworkshop\b/i,
  /\btrain(?:ing)?\b/i,
  /\bupskill(?:ing)?\b/i,
  /\bteach (?:my|our|the) team\b/i,
  /\bemployees? need(?:s)? to learn\b/i,
  /\bteam(?:s)? (?:needs?|need) to learn\b/i,
  /\badoption\b/i,
  /\bkurs\b/i,
  /\bbootcamp\b/i,
  /\bopplæring\b/i,
  /\bopplaering\b/i,
  /\bopplaring\b/i,
]

const PAIN_PATTERNS = [
  /\bpain\b/i,
  /\bproblem\b/i,
  /\bchallenge\b/i,
  /\bbottleneck\b/i,
  /\bmanual\b/i,
  /\bslow\b/i,
  /\bwast(?:e|ing)\b/i,
  /\bmiss(?:ed|ing)?\b/i,
  /\bstruggl/i,
  /\bneed(?:s)?\b/i,
  /\brotting\b/i,
  /\binbox\b/i,
  /\bno crm\b/i,
  /\bleads?\s+(?:rotting|going cold|go cold|cold)\b/i,
  /\bform submissions?\b/i,
]

const HIGH_INTENT_PATTERNS = [
  /\bbook\b/i,
  /\bschedule\b/i,
  /\bcall\b/i,
  /\bpricing\b/i,
  /\bproposal\b/i,
  /\bready to\b/i,
  /\bthis quarter\b/i,
  /\basap\b/i,
  /\burgent\b/i,
  /\bneed to implement\b/i,
]

const MEDIUM_INTENT_PATTERNS = [
  /\blooking to\b/i,
  /\bwant to\b/i,
  /\bneed(?:s)? to\b/i,
  /\btrying to\b/i,
  /\bexploring\b/i,
  /\binvest\b/i,
  /\bbudget\b/i,
  /\bquarter\b/i,
]

const EVIDENCE_PATTERNS = [
  /\bdashboard\b/i,
  /\breport\b/i,
  /\bspreadsheet\b/i,
  /\bcsv\b/i,
  /\bexcel\b/i,
  /\bdocument\b/i,
  /\bcrm\b/i,
  /\banalytics\b/i,
  /\bwebsite\b/i,
  /\burl\b/i,
]

function textFromMessages(messages?: ChatMessage[]): string {
  if (!Array.isArray(messages)) return ''
  return messages
    .filter(message => message.role === 'user')
    .map(message => String(message.content || ''))
    .join('\n')
}

function getEvidenceText(conversationFlow?: Partial<ConversationFlowState>): string {
  if (!conversationFlow?.evidence) return ''
  return Object.values(conversationFlow.evidence)
    .flatMap(value => Array.isArray(value) ? value : [])
    .join('\n')
}

function includesAny(text: string, patterns: RegExp[]): boolean {
  return patterns.some(pattern => pattern.test(text))
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function normalizeCompanySize(size: unknown, employeeCount?: unknown): string {
  if (typeof employeeCount === 'number' && Number.isFinite(employeeCount)) {
    return String(employeeCount)
  }
  return typeof size === 'string' ? size : ''
}

function detectIdentityConfidence(intelligenceContext?: IntelligenceContext): ConsultantState['identityConfidence'] {
  if (!intelligenceContext) return 'none'
  const record = intelligenceContext as IntelligenceContext & { identityConfirmed?: unknown }
  if (record.identityConfirmed === true) return 'verified'
  if (intelligenceContext.company?.name || intelligenceContext.person?.fullName || intelligenceContext.role) {
    return 'provisional'
  }
  return 'none'
}

function detectSegment(text: string, intelligenceContext?: IntelligenceContext): ConsultantSegment {
  const role = `${intelligenceContext?.person?.role || ''} ${intelligenceContext?.role || ''}`
  const size = normalizeCompanySize(
    intelligenceContext?.company?.size,
    intelligenceContext?.company?.employeeCount
  )
  const combined = `${text}\n${role}\n${intelligenceContext?.company?.industry || ''}\n${size}`

  if (includesAny(combined, INTERN_PATTERNS)) return 'intern_student'
  if (includesAny(combined, ENTERPRISE_PATTERNS) || /\b[5-9]\d\d\d\b/.test(size)) return 'enterprise_buyer'
  if (/\bcto\b|\bchief technology\b|\bproduct lead\b|\bhead of product\b|\bengineering\b/i.test(role)) {
    return 'cto_product_lead'
  }
  if (includesAny(combined, MARKETING_PATTERNS) || /\bmarketing manager\b/i.test(role)) return 'marketing_manager'
  if (/\bfounder\b|\bstartup\b|\bmvp\b/i.test(combined)) return 'startup_founder'
  if (includesAny(combined, LOCAL_SERVICE_PATTERNS)) return 'local_service_company'
  if (/\bowner\b|\bsmall business\b|\blocal business\b/i.test(combined)) return 'small_business_owner'
  return 'unknown'
}

function detectBuyingIntent(text: string, intelligenceContext?: IntelligenceContext): BuyingIntent {
  if (intelligenceContext?.pitchTriggeredByUser === true) return 'high'
  if (intelligenceContext?.budget?.hasExplicit === true) return 'high'
  if (includesAny(text, HIGH_INTENT_PATTERNS)) return 'high'
  if (includesAny(text, MEDIUM_INTENT_PATTERNS)) return 'medium'
  if (text.trim().length > 0) return 'low'
  return 'none'
}

function detectUrgency(text: string, intelligenceContext?: IntelligenceContext): ConsultantState['urgency'] {
  const urgency = intelligenceContext?.timeline?.urgency ?? intelligenceContext?.budget?.urgency
  if (typeof urgency === 'number') {
    if (urgency >= 0.75) return 'high'
    if (urgency >= 0.35) return 'medium'
  }
  if (/\b(asap|urgent|this quarter|q1|q2|by next month|immediately)\b/i.test(text)) return 'high'
  if (/\b(this year|next quarter|soon|within \d+ (?:weeks|months))\b/i.test(text)) return 'medium'
  return 'low'
}

function detectMultimodalOpportunity(text: string): MultimodalOpportunity {
  if (/\b(website|landing page|conversion|url|homepage|page)\b/i.test(text)) return 'url'
  if (/\b(report|spreadsheet|csv|excel|document|deck|pdf|campaign report)\b/i.test(text)) return 'upload'
  if (/\b(hubspot|crm|salesforce|google analytics|ga4|dashboard|looker|gmail|support inbox|zendesk|workflow)\b/i.test(text)) {
    return 'screen_share'
  }
  if (/\b(factory floor|warehouse|machine|equipment|physical process|camera|webcam|show you)\b/i.test(text)) {
    return 'webcam'
  }
  return null
}

function detectServiceFit(params: {
  text: string
  segment: ConsultantSegment
  buyingIntent: BuyingIntent
  intelligenceContext?: IntelligenceContext | undefined
}): ServiceFitMode {
  const { text, segment, intelligenceContext } = params
  const companySize = normalizeCompanySize(
    intelligenceContext?.company?.size,
    intelligenceContext?.company?.employeeCount
  )

  if (segment === 'intern_student') return 'no_sale_education'
  if (segment === 'enterprise_buyer' && includesAny(`${text}\n${companySize}`, ENTERPRISE_PATTERNS)) {
    return 'enterprise_transformation'
  }
  if (includesAny(text, WORKSHOP_PATTERNS)) {
    if (segment === 'local_service_company') return 'implementation_sprint'
    return segment === 'small_business_owner' ? 'basic_ai_workshop' : 'team_workshop'
  }
  if (includesAny(text, CRM_AUTOMATION_PATTERNS)) {
    return segment === 'enterprise_buyer' ? 'consulting_advisory' : 'implementation_sprint'
  }
  if (/\b(custom|build|implementation|integrate|platform|system|architecture|roadmap|strategy)\b/i.test(text)) {
    return segment === 'enterprise_buyer' ? 'enterprise_transformation' : 'consulting_advisory'
  }
  if (segment === 'local_service_company' && includesAny(text, PAIN_PATTERNS)) return 'implementation_sprint'
  return 'diagnostic'
}

function getFitScoreForServiceFit(serviceFit: ServiceFitMode): { workshop: number; consulting: number } {
  switch (serviceFit) {
    case 'basic_ai_workshop':
      return { workshop: 0.78, consulting: 0.25 }
    case 'team_workshop':
      return { workshop: 0.86, consulting: 0.32 }
    case 'implementation_sprint':
      return { workshop: 0.42, consulting: 0.78 }
    case 'consulting_advisory':
      return { workshop: 0.28, consulting: 0.84 }
    case 'enterprise_transformation':
      return { workshop: 0.24, consulting: 0.92 }
    case 'no_sale_education':
      return { workshop: 0.08, consulting: 0.05 }
    default:
      return DEFAULT_FIT_SCORE
  }
}

function getSuggestedNextCategory(params: {
  corePainCaptured: boolean
  buyingIntent: BuyingIntent
  evidenceAvailable: boolean
  multimodalOpportunity: MultimodalOpportunity
  serviceFit: ServiceFitMode
  askedForWeekOnePlan?: boolean
}): ConversationCategory | null {
  if (params.serviceFit === 'no_sale_education') return null
  if (params.askedForWeekOnePlan) return null
  if (!params.corePainCaptured) return 'pain'
  // Bottleneck is known. Coverage gaps stay private — do not interview the next form field.
  return null
}

function getConversationObjective(params: {
  corePainCaptured: boolean
  serviceFit: ServiceFitMode
  bookingReadiness: ConsultantState['bookingReadiness']
  multimodalOpportunity: MultimodalOpportunity
  evidenceAvailable: boolean
  totalUserTurns: number
  askedForWeekOnePlan?: boolean
}): ConversationObjective {
  if (params.askedForWeekOnePlan && params.corePainCaptured) return 'recommend'
  if (!params.corePainCaptured) return 'diagnose'
  if (params.serviceFit === 'no_sale_education') return 'prove-value'
  if (
    params.bookingReadiness === 'ready' &&
    params.totalUserTurns >= MIN_USER_TURNS_BEFORE_SCORING
  ) {
    return 'close'
  }
  if (params.askedForWeekOnePlan) return 'recommend'
  if (params.multimodalOpportunity && !params.evidenceAvailable) return 'prove-value'
  if (params.serviceFit !== 'diagnostic') return 'recommend'
  return 'validate'
}

export function analyzeConsultantState({
  messages,
  conversationFlow,
  intelligenceContext,
  multimodalContext,
}: AnalyzeConsultantStateParams): ConsultantState {
  const messageText = textFromMessages(messages)
  const evidenceText = getEvidenceText(conversationFlow)
  const companyText = [
    intelligenceContext?.company?.name,
    intelligenceContext?.company?.industry,
    intelligenceContext?.company?.summary,
    intelligenceContext?.person?.role,
    intelligenceContext?.role,
  ]
    .filter(Boolean)
    .join('\n')
  const text = `${messageText}\n${evidenceText}\n${companyText}`
  const askedForWeekOnePlan = isWeekOnePlanAsk(lastUserTextFromMessages(messages))
  const segment = detectSegment(text, intelligenceContext)
  const corePainCaptured = conversationFlow?.covered?.pain === true || includesAny(text, PAIN_PATTERNS)
  const buyingIntent = detectBuyingIntent(text, intelligenceContext)
  const urgency = detectUrgency(text, intelligenceContext)
  const multimodalOpportunity = detectMultimodalOpportunity(text)
  const serviceFit = detectServiceFit({ text, segment, buyingIntent, intelligenceContext })
  const evidenceAvailable =
    multimodalContext?.hasRecentImages === true ||
    multimodalContext?.hasRecentUploads === true
  const bookingReadiness =
    serviceFit !== 'no_sale_education' &&
    serviceFit !== 'diagnostic' &&
    corePainCaptured &&
    (buyingIntent === 'high' || urgency === 'high' || askedForWeekOnePlan)
      ? 'ready'
      : serviceFit !== 'no_sale_education' &&
          serviceFit !== 'diagnostic' &&
          corePainCaptured &&
          buyingIntent === 'medium'
        ? 'emerging'
        : 'not_ready'
  const shouldOfferServiceFit =
    serviceFit !== 'no_sale_education' &&
    serviceFit !== 'diagnostic' &&
    corePainCaptured &&
    (buyingIntent === 'medium' || buyingIntent === 'high' || askedForWeekOnePlan)
  const suggestedNextCategory = getSuggestedNextCategory({
    corePainCaptured,
    buyingIntent,
    evidenceAvailable,
    multimodalOpportunity,
    serviceFit,
    askedForWeekOnePlan,
  })
  const conversationObjective = getConversationObjective({
    corePainCaptured,
    serviceFit,
    bookingReadiness,
    multimodalOpportunity,
    evidenceAvailable,
    totalUserTurns: conversationFlow?.totalUserTurns ?? 0,
    askedForWeekOnePlan,
  })
  const fitScore = getFitScoreForServiceFit(serviceFit)

  const signals = [
    `segment:${segment}`,
    `serviceFit:${serviceFit}`,
    `intent:${buyingIntent}`,
    `urgency:${urgency}`,
    multimodalOpportunity ? `multimodal:${multimodalOpportunity}` : '',
    evidenceAvailable ? 'evidence:available' : '',
    includesAny(text, EVIDENCE_PATTERNS) ? 'evidence:mentioned' : '',
  ].filter(Boolean)

  return {
    identityConfidence: detectIdentityConfidence(intelligenceContext),
    segment,
    corePainCaptured,
    buyingIntent,
    urgency,
    serviceFit,
    evidenceAvailable,
    multimodalOpportunity,
    bookingReadiness,
    shouldOfferServiceFit,
    suggestedNextCategory,
    conversationObjective,
    fitScore,
    signals,
  }
}

export function mergeFitScores(
  existing: unknown,
  consultantState: ConsultantState
): { workshop: number; consulting: number } {
  const existingScore =
    existing && typeof existing === 'object'
      ? existing as Record<string, unknown>
      : {}
  const existingWorkshop =
    typeof existingScore.workshop === 'number' && Number.isFinite(existingScore.workshop)
      ? existingScore.workshop
      : 0
  const existingConsulting =
    typeof existingScore.consulting === 'number' && Number.isFinite(existingScore.consulting)
      ? existingScore.consulting
      : 0

  return {
    workshop: clamp01(Math.max(existingWorkshop, consultantState.fitScore.workshop)),
    consulting: clamp01(Math.max(existingConsulting, consultantState.fitScore.consulting)),
  }
}

export function getPitchStageForServiceFit(
  serviceFit: ServiceFitMode,
  segment?: ConsultantSegment
): 'WORKSHOP_PITCH' | 'CONSULTING_PITCH' | null {
  if (
    segment === 'intern_student' ||
    segment === 'local_service_company'
  ) {
    if (serviceFit === 'basic_ai_workshop' || serviceFit === 'team_workshop') {
      return null
    }
  }
  switch (serviceFit) {
    case 'basic_ai_workshop':
    case 'team_workshop':
      return 'WORKSHOP_PITCH'
    case 'implementation_sprint':
    case 'consulting_advisory':
    case 'enterprise_transformation':
      return 'CONSULTING_PITCH'
    default:
      return null
  }
}

export function getServiceFitPitchConfig(serviceFit: ServiceFitMode): ServiceFitPitchConfig {
  switch (serviceFit) {
    case 'basic_ai_workshop':
      return {
        product: 'workshop',
        serviceMode: serviceFit,
        name: 'Basic AI Workshop',
        priceRange: 'discuss after fit is confirmed; do not quote package pricing unless asked',
        duration: 'short practical enablement session',
        format: 'hands-on local-business workshop',
        bestFor: 'owners and small teams that need practical AI literacy and quick wins',
        positioning: 'Teach the team where AI helps immediately without overbuilding.',
      }
    case 'team_workshop':
      return {
        product: 'workshop',
        serviceMode: serviceFit,
        name: 'Team AI Workshop',
        priceRange: 'discuss on the call after team size and goals are clear',
        duration: 'focused team enablement',
        format: 'hands-on workshop',
        bestFor: 'teams that need shared AI habits, examples, and safe adoption patterns',
        positioning: 'Turn scattered AI curiosity into practical team workflows.',
      }
    case 'implementation_sprint':
      return {
        product: 'consulting',
        serviceMode: serviceFit,
        name: 'AI Implementation Sprint',
        priceRange: 'scope after reviewing the workflow; avoid detailed pricing before the call',
        duration: 'short sprint',
        format: 'workflow diagnosis and implementation support',
        bestFor: 'businesses with a concrete CRM, inbox, analytics, or scheduling bottleneck',
        positioning: 'Map the workflow, pick the highest-leverage automation, and implement the first useful slice.',
      }
    case 'enterprise_transformation':
      return {
        product: 'consulting',
        serviceMode: serviceFit,
        name: 'Enterprise AI Transformation',
        priceRange: 'enterprise scope only after governance, security, and change-management discovery',
        duration: 'phased advisory and implementation',
        format: 'executive advisory plus enablement roadmap',
        bestFor: 'larger organizations balancing adoption, governance, security, and change management',
        positioning: 'Create a responsible AI adoption path that leadership, security, and teams can trust.',
      }
    case 'consulting_advisory':
      return {
        product: 'consulting',
        serviceMode: serviceFit,
        name: 'AI Consulting Advisory',
        priceRange: 'scope on the call; do not invent pricing',
        duration: 'advisory engagement',
        format: 'diagnosis, roadmap, and implementation guidance',
        bestFor: 'leaders with a real operational bottleneck and enough complexity to need advisory support',
        positioning: 'Find the shortest path from fuzzy AI ambition to a concrete business workflow.',
      }
    case 'no_sale_education':
      return {
        product: 'workshop',
        serviceMode: serviceFit,
        name: 'Educational Guidance',
        priceRange: 'do not quote pricing',
        duration: 'light guidance',
        format: 'helpful educational response',
        bestFor: 'students, interns, or low-buying-authority visitors',
        positioning: 'Be useful without pushing paid consulting.',
      }
    default:
      return {
        product: 'consulting',
        serviceMode: 'diagnostic',
        name: 'Workflow Diagnostic Call',
        priceRange: 'not enough signal to quote pricing',
        duration: '30-minute diagnostic',
        format: 'fit clarification',
        bestFor: 'visitors where the problem, authority, or evidence is still unclear',
        positioning: 'Clarify whether workshop, implementation, consulting, or education is the right next step.',
      }
  }
}
